import React, { useMemo, useState } from "react";
import { useInfiniteScroll } from "../../../../common/hooks/use-infinite-scroll";
import { useBtmRequestTable } from "./use-btm-request-table";
import { Paper, Skeleton, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { classNamesList } from "../../../../common/utils/class-name";
import { flexRender } from "@tanstack/react-table";
import { Table as TableType } from "@tanstack/table-core/build/lib/types";
import { BtmReturnRequestListItem } from "../model/btm-return-request-list-item";
import { BtmReturnRequestStatus } from "../model/btm-return-request-status";
import { BtmReturnRequestType } from "../model/btm-return-request-type";
import { BtmReturnRequestCategory } from "../model/btm-return-request-category";

import "./style.scss";

interface IBtmRequestTableProps {
    data: BtmReturnRequestListItem[] | undefined;
    withStatus: boolean;
    withActionColumn: boolean;
    onBtmRequestOpen: (request: BtmReturnRequestListItem) => void;
    columnFilters: {
        quickSearch?: string;
        status?: BtmReturnRequestStatus;
        type?: BtmReturnRequestType;
        category?: BtmReturnRequestCategory;
    };
    isLoading: boolean;
}

interface IBaseTableBody {
    table: TableType<BtmReturnRequestListItem>;
}

interface ITableBody extends IBaseTableBody {
    onRowClick: (row: BtmReturnRequestListItem) => void;
}

export const BtmRequestTable = (props: IBtmRequestTableProps) => {
    const defaultPageSize = 25;
    const [endCursor, setEndCursor] = useState(defaultPageSize);

    const memoizedData = useMemo(() => props.data ?? [], [props.data]);
    const table = useBtmRequestTable(memoizedData, props.withStatus, props.withActionColumn, endCursor, props.columnFilters);

    const sentinelRef = useInfiniteScroll<HTMLDivElement>({
        onLoadMore: () => setEndCursor(prevState => prevState + defaultPageSize),
        hasMore: props.data ? props.data.length > endCursor : false,
        rootMargin: 200,
    });

    return <Paper elevation={0} className={"page"}>
        <div className={"search-list"}>
            <Table cellPadding={0} cellSpacing={0} className={classNamesList(["search-list__table",
                props.isLoading ? "search-list__table--loading" : ""])}>
                <TableHead>
                    <TableRow key={"header"}>
                        {table.getHeaderGroups()[0].headers.map(header =>
                            header.isPlaceholder ? null :
                                <TableCell key={header.id}>
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                </TableCell>)}
                    </TableRow>
                </TableHead>
                {!props.data ?
                    <TableBodyPlaceholder table={table} /> :
                    <TableBodyData table={table} onRowClick={props.onBtmRequestOpen} />}
            </Table>
            <div ref={sentinelRef} style={{height: "1px"}} />
        </div>
    </Paper>;
};

const TableBodyPlaceholder = (props: IBaseTableBody) => {
    return <TableBody>
        {new Array(10).fill(0).map((_, rowIndex) =>
            <TableRow key={`placeholder-row-${rowIndex}`} className={"placeholder"}>
                {props.table.getHeaderGroups()[0].headers.map(header =>
                    <TableCell key={`placeholder-cell-${rowIndex}-${header.id}`}>
                        <Skeleton height={50} width={"80%"} />
                    </TableCell>)}
            </TableRow>)}
    </TableBody>;
};

const TableBodyData = (props: ITableBody) => {
    return <TableBody>
        {props.table.getPaginationRowModel().rows.map(row =>
            <TableRow key={row.id} onClick={() => props.onRowClick(row.original)}>
                {row.getVisibleCells().map(cell =>
                    <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>)}
            </TableRow>)}
    </TableBody>;
};
